import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { subjects, type AuthUser } from "@facturar/auth";
import {
  authClient,
  REDIRECT_URI,
  clearTokens,
  loadTokens,
  pushPostLoginRedirect,
  saveChallenge,
  saveTokens,
} from "@/lib/auth-client";
import { setOnUnauthorized } from "@/lib/api";

type AuthState =
  | { status: "loading"; user: null }
  | { status: "unauthenticated"; user: null }
  | { status: "authenticated"; user: AuthUser };

type AuthContextValue = AuthState & {
  /** Trigger PKCE redirect to the OpenAuth issuer. */
  signIn: (returnTo?: string) => Promise<void>;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * AuthProvider — verifies the persisted access/refresh tokens against
 * `auth.pahfacturar.com` on mount, transparently refreshes them, and exposes
 * `signIn` / `signOut`. Storage keys live under `pahfacturar.app.*`, subjects
 * come from the `@facturar/auth` package.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: "loading",
    user: null,
  });
  // Lock so signIn() can never start two PKCE flows in the same page lifetime.
  // See the comment inside signIn for why this matters.
  const signInInFlightRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const tokens = loadTokens();
    if (!tokens) {
      setState({ status: "unauthenticated", user: null });
      return;
    }
    void (async () => {
      try {
        const verified = await authClient.verify(subjects, tokens.access, {
          refresh: tokens.refresh,
        });
        if (cancelled) return;
        if (verified.err) {
          clearTokens();
          setState({ status: "unauthenticated", user: null });
          return;
        }
        if (verified.tokens) {
          saveTokens({
            access: verified.tokens.access,
            refresh: verified.tokens.refresh,
          });
        }
        setState({
          status: "authenticated",
          user: verified.subject.properties as AuthUser,
        });
      } catch {
        if (cancelled) return;
        clearTokens();
        setState({ status: "unauthenticated", user: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (returnTo?: string) => {
    // One-shot per page. The PKCE redirect is a hard navigation; if a
    // second caller (e.g. RequireAuth re-mounting, or a 401 from a query
    // that races with the verify-on-mount) fires signIn() again before
    // window.location.href has navigated, we'd overwrite the saved
    // challenge in sessionStorage and the auth-callback exchange would
    // mismatch the verifier the issuer remembers. Lock here.
    if (signInInFlightRef.current) return;
    signInInFlightRef.current = true;
    if (returnTo) pushPostLoginRedirect(returnTo);
    const { challenge, url } = await authClient.authorize(REDIRECT_URI, "code", {
      pkce: true,
      provider: "code",
    });
    saveChallenge(challenge);
    window.location.href = url;
  }, []);

  const signOut = useCallback(() => {
    clearTokens();
    setState({ status: "unauthenticated", user: null });
    window.location.assign("/login");
  }, []);

  // Wire fetchApiWithAuth's 401 handler to bounce the user back through
  // OpenAuth when the access token expires mid-session. Without this every
  // failed query renders a generic "connection error" card and the user has
  // no way to recover except a manual refresh + login.
  //
  // Defensive gate: never fire signIn() while we're sitting on /auth/callback
  // or /login. Those pages own the PKCE flow themselves — bouncing from
  // them would overwrite the in-flight challenge in sessionStorage and
  // mismatch the verifier on the next exchange. Same goes for any 401
  // landing during the brief window where AuthContext is still verifying
  // tokens (status === "loading").
  useEffect(() => {
    setOnUnauthorized(() => {
      const path = window.location.pathname;
      if (path.startsWith("/auth/") || path === "/login") return;
      clearTokens();
      void signIn(path + window.location.search);
    });
    return () => setOnUnauthorized(null);
  }, [signIn]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signOut }),
    [state, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

/** Asserts an authenticated user. Use inside RequireAuth-wrapped routes. */
export function useAuthUser(): AuthUser {
  const auth = useAuth();
  if (auth.status !== "authenticated") {
    throw new Error("useAuthUser called from a non-authenticated context");
  }
  return auth.user;
}
